import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Sparkles,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Check,
  Copy,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

interface BulkRow {
  id: string;
  theme: string;
  post_type: string;
  image_url: string;
  caption?: string;
  hashtags?: string[];
  status: 'pending' | 'loading' | 'done' | 'error';
  error_msg?: string;
}

const POST_TYPES = ['Feed Post', 'Reel', 'Carousel', 'Story'];

const blankRow = (i: number): BulkRow => ({
  id: `b-${Date.now()}-${i}`,
  theme: '',
  post_type: 'Feed Post',
  image_url: '',
  status: 'pending',
});

const SAMPLE_BULK: BulkRow[] = [
  { id: 'sb-1', theme: 'Sunrise over the cliffs', post_type: 'Reel', image_url: '', status: 'pending' },
  { id: 'sb-2', theme: 'A slow lunch by the sea', post_type: 'Carousel', image_url: '', status: 'pending' },
  { id: 'sb-3', theme: 'Hidden village at golden hour', post_type: 'Feed Post', image_url: '', status: 'pending' },
];

const BulkCaptionTool: React.FC = () => {
  const [rows, setRows] = useState<BulkRow[]>(SAMPLE_BULK);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const updateRow = (id: string, patch: Partial<BulkRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, blankRow(prev.length)]);
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));
  const loadSample = () => setRows(SAMPLE_BULK.map((r) => ({ ...r, status: 'pending' as const, caption: undefined, hashtags: undefined })));
  const clearAll = () => {
    if (!confirm('Clear all rows?')) return;
    setRows([]);
  };

  const runAll = async () => {
    const valid = rows.filter((r) => r.theme.trim());
    if (valid.length === 0) {
      toast.error('Add at least one row with a theme.');
      return;
    }
    setRunning(true);
    setProgress({ done: 0, total: valid.length });
    let done = 0;

    for (const r of valid) {
      updateRow(r.id, { status: 'loading', error_msg: undefined });
      try {
        const { data, error } = await supabase.functions.invoke('caption-suggest', {
          body: {
            theme: r.theme,
            imageUrl: r.image_url,
            dayLabel: 'Bulk',
            postType: r.post_type,
            existingCaption: '',
          },
        });
        if (error) throw new Error(error.message);
        if (!data || data.error) throw new Error(data?.error || 'No result');
        // Pick the first caption (Cinematic) by default; user can regenerate row if needed
        const caption = data.captions?.[0]?.caption || '';
        const allTags = [
          ...(data.hashtags?.broad || []),
          ...(data.hashtags?.mid || []),
          ...(data.hashtags?.niche || []),
        ];
        updateRow(r.id, { status: 'done', caption, hashtags: allTags });
      } catch (e: any) {
        updateRow(r.id, { status: 'error', error_msg: e?.message || 'Generation failed' });
      }
      done++;
      setProgress({ done, total: valid.length });
    }
    setRunning(false);
    toast.success(`Generated ${done} caption${done === 1 ? '' : 's'}`);
  };

  const copyRow = async (r: BulkRow) => {
    const text = `${r.caption}\n\n${(r.hashtags || []).join(' ')}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied caption + hashtags');
    } catch {
      toast.error('Copy failed');
    }
  };

  const exportCSV = () => {
    const header = 'Theme,Type,Caption,Hashtags';
    const lines = rows
      .filter((r) => r.caption)
      .map(
        (r) =>
          `"${r.theme.replace(/"/g, "'")}","${r.post_type}","${(r.caption || '').replace(/"/g, "'").replace(/\n/g, ' ')}","${(r.hashtags || []).join(' ')}"`,
      );
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-captions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const doneCount = rows.filter((r) => r.status === 'done').length;
  const errorCount = rows.filter((r) => r.status === 'error').length;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-base text-[#1A3B5C]">Bulk Caption Generator</h3>
          <p className="font-sans text-xs text-slate-500 mt-0.5">
            Paste a list of post themes and run the AI on all of them at once. Captions + 30 hashtags
            per row.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadSample}
            disabled={running}
            className="font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#C9A961] hover:text-[#C9A961] disabled:opacity-40 transition-colors"
          >
            Load Sample
          </button>
          <button
            onClick={clearAll}
            disabled={running}
            className="font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-40 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={addRow}
            disabled={running}
            className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-wider uppercase px-3 py-2 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white disabled:opacity-40 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add Row
          </button>
          <button
            onClick={runAll}
            disabled={running || rows.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] disabled:opacity-40 transition-all"
          >
            {running ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress.done}/{progress.total}
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" /> Run All
              </>
            )}
          </button>
        </div>
      </div>

      {running && (
        <div className="bg-white border border-slate-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-sans text-[10px] tracking-wider uppercase text-slate-500">
              Generating
            </span>
            <span className="font-sans text-xs text-slate-600">
              {progress.done} of {progress.total}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#C9A961] to-[#8B6F47] transition-all"
              style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="bg-white border border-slate-200 py-16 text-center font-sans text-sm text-slate-400">
            No rows yet. Click <em>Add Row</em> or <em>Load Sample</em> to get started.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-3 mb-3">
              <div>
                <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">
                  Theme
                </label>
                <input
                  value={r.theme}
                  onChange={(e) => updateRow(r.id, { theme: e.target.value })}
                  placeholder="What's this post about?"
                  className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] outline-none"
                />
              </div>
              <div>
                <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">
                  Type
                </label>
                <select
                  value={r.post_type}
                  onChange={(e) => updateRow(r.id, { post_type: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm text-[#0D1B2A] focus:border-[#C9A961] outline-none"
                >
                  {POST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">
                  Image URL (optional)
                </label>
                <input
                  value={r.image_url}
                  onChange={(e) => updateRow(r.id, { image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] outline-none"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => removeRow(r.id)}
                  disabled={running}
                  className="p-2 text-slate-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                  title="Remove row"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {r.status === 'loading' && (
              <div className="bg-slate-50 border border-slate-200 p-3 inline-flex items-center gap-2 font-sans text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C9A961]" /> Generating...
              </div>
            )}

            {r.status === 'error' && (
              <div className="bg-red-50 border border-red-200 p-3 inline-flex items-start gap-2 font-sans text-xs text-red-700">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                {r.error_msg}
              </div>
            )}

            {r.status === 'done' && r.caption && (
              <div className="bg-slate-50 border border-slate-200 border-l-4 border-l-[#C9A961] p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-emerald-600 font-semibold inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Done
                  </span>
                  <button
                    onClick={() => copyRow(r)}
                    className="inline-flex items-center gap-1 px-2 py-1 font-sans text-[10px] tracking-wider uppercase text-slate-600 hover:text-[#1A3B5C] transition-colors"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <p className="font-sans text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {r.caption}
                </p>
                <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-200">
                  {(r.hashtags || []).slice(0, 30).map((t, i) => (
                    <span
                      key={i}
                      className="inline-block px-1.5 py-0.5 bg-white border border-slate-200 font-sans text-[10px] text-slate-600"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div className="font-sans text-[10px] text-slate-400">
                  {(r.hashtags || []).length} hashtags · {r.caption.length} chars
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary footer */}
      {(doneCount > 0 || errorCount > 0) && (
        <div className="bg-[#F5F1E8] border border-[#C9A961]/30 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="font-sans text-xs text-[#8B6F47]">
            <span className="font-semibold">{doneCount}</span> generated
            {errorCount > 0 && (
              <span className="ml-2 text-red-600">· {errorCount} failed</span>
            )}
          </div>
          <button
            onClick={exportCSV}
            disabled={doneCount === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] disabled:opacity-40 transition-colors"
          >
            <Download className="w-3 h-3" /> Download CSV
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkCaptionTool;
